import React, { useEffect } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiUpload } from 'react-icons/fi';
import { uploadAndParseBill, clearParsedData } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';
import ParsedDataView from './ParsedDataView';

const SUPPORTED_FORMATS = ['application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const UploadBillTab = ({ onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { uploadStatus, parseStatus, parsedData } = useSelector((state) => state.billParsing);

  const schema = yup.object().shape({
    selectedFile: yup
      .mixed()
      .required(t('File is required'))
      .test('fileFormat', t('Please select a PDF file'), (value) => {
        return value && SUPPORTED_FORMATS.includes(value.type);
      })
      .test('fileSize', t('File size must be less than 10MB'), (value) => {
        return value && value.size <= MAX_FILE_SIZE;
      })
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  useEffect(() => {
    if (!parsedData) {
      reset();
    }
  }, [parsedData, reset]);

  const isUploading = uploadStatus === LoadingState.pending;
  const isParsing = parseStatus === LoadingState.pending;
  const isLoading = isUploading || isParsing;

  const onSubmit = async (data) => {
    await dispatch(uploadAndParseBill({ orgId, file: data.selectedFile }));
  };

  const handleCancel = () => {
    dispatch(clearParsedData());
    setValue('selectedFile', null);
  };

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-3">{t('Upload PDF Bill')}</h5>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group>
              <Controller
                name="selectedFile"
                control={control}
                render={({ field }) => {
                  const handleSelectFile = (e) => field.onChange(e.target.files[0]);
                  return (
                    <>
                      <Form.File
                        id="billFileUploadControl"
                        label={t('Select PDF File')}
                        accept=".pdf,application/pdf"
                        className="form-content"
                        onChange={handleSelectFile}
                        isInvalid={!!errors.selectedFile}
                        disabled={isLoading || parsedData}
                      />
                      {errors.selectedFile && <span className="required-span">{errors.selectedFile.message}</span>}
                    </>
                  );
                }}
              />
            </Form.Group>

            {isLoading && (
              <Alert variant="info" className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="mr-2" />
                <span>{t('Processing...')}</span>
              </Alert>
            )}

            <Button
              type="submit"
              variant="secondary"
              disabled={!isValid || isLoading || parsedData}
              className="d-flex align-items-center"
            >
              <FiUpload className="mr-2" />
              {t('Upload & Parse')}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {parsedData && <ParsedDataView parsedData={parsedData} onCancel={handleCancel} onSuccess={onSuccess} />}
    </div>
  );
};

export default UploadBillTab;
